import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Skeleton from '@mui/material/Skeleton';
import { LTHeader, LTItem, LTMobileItem, LTPlaceholder, LTRow } from 'components/common/ListTable';
import { RootState } from 'redux/store';
import { CustomLTRow } from '../../../../../customLTRow/customLTRow';
import * as S from '../../../../../Table.styled';

export type CompaniesTableCompany = {
    id: number;
    company_name: string;
    email: string;
    date_added: string;
    number_of_clients: number;
};

type CompaniesTableProps = {
    companies: CompaniesTableCompany[];
    mobile: boolean;
    isLoading?: boolean;
};

const CompaniesTable = ({ companies, mobile, isLoading = false }: CompaniesTableProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();

    const skeletonArray = companies.length > 0 ? companies : [1, 2, 3];
    const desktopTableMinWidth = '100%';
    const companiesGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: '110px minmax(220px, 1.4fr) minmax(260px, 1.5fr) 150px 150px',
        alignItems: 'center',
        width: desktopTableMinWidth,
    };

    return (
        <S.ListTable
            style={
                mobile
                    ? { maxHeight: 'unset', width: '100%', maxWidth: '1000px' }
                    : { maxHeight: 'unset', width: '97vw', marginTop: '36px' }
            }
        >
            <LTHeader
                style={{ paddingLeft: '16px', minWidth: desktopTableMinWidth }}
                innerStyle={mobile ? undefined : companiesGridStyle}
            >
                <LTItem style={mobile ? undefined : { width: '100%' }}>ID</LTItem>
                <LTItem style={mobile ? undefined : { width: '100%' }}>
                    {t('name', { lng: language })}
                </LTItem>
                <LTItem style={mobile ? undefined : { width: '100%' }}>
                    {t('email', { lng: language })}
                </LTItem>
                <LTItem style={mobile ? undefined : { width: '100%' }}>Date Added</LTItem>
                <LTItem style={mobile ? undefined : { width: '100%' }}>Number of Clients</LTItem>
            </LTHeader>

            {!isLoading && companies.length === 0 && (
                <LTPlaceholder>{t('theListIsEmpty', { lng: language })}</LTPlaceholder>
            )}

            {!isLoading &&
                companies.length > 0 &&
                companies.map((company) => (
                    <CustomLTRow
                        key={`company_${company.id}`}
                        id={`company_${company.id}`}
                        style={
                            mobile
                                ? { paddingLeft: '16px' }
                                : {
                                      paddingLeft: '16px',
                                      width: desktopTableMinWidth,
                                      minWidth: desktopTableMinWidth,
                                      maxWidth: desktopTableMinWidth,
                                  }
                        }
                        wrapperStyle={mobile ? undefined : companiesGridStyle}
                    >
                        <LTItem style={mobile ? undefined : { width: '100%' }}>{company.id}</LTItem>
                        <LTItem
                            tip={company.company_name}
                            style={mobile ? undefined : { width: '100%' }}
                        >
                            <S.Link to={`/dashboard/${company.id}/organization`}>
                                {company.company_name}
                            </S.Link>
                        </LTItem>
                        <LTItem tip={company.email} style={mobile ? undefined : { width: '100%' }}>
                            {company.email}
                        </LTItem>
                        <LTItem style={mobile ? undefined : { width: '100%' }}>
                            {company.date_added}
                        </LTItem>
                        <LTItem style={mobile ? undefined : { width: '100%' }}>
                            {company.number_of_clients}
                        </LTItem>

                        <LTMobileItem style={{ minWidth: '132px' }} name="ID">
                            {company.id}
                        </LTMobileItem>
                        <LTMobileItem
                            style={{ minWidth: '132px' }}
                            name={t('name', { lng: language })}
                        >
                            <S.Link to={`/dashboard/${company.id}/organization`}>
                                {company.company_name}
                            </S.Link>
                        </LTMobileItem>
                        <LTMobileItem
                            style={{ minWidth: '132px' }}
                            name={t('email', { lng: language })}
                        >
                            {company.email}
                        </LTMobileItem>
                        <LTMobileItem style={{ minWidth: '132px' }} name="Date Added">
                            {company.date_added}
                        </LTMobileItem>
                        <LTMobileItem style={{ minWidth: '132px' }} name="Number of Clients">
                            {company.number_of_clients}
                        </LTMobileItem>
                    </CustomLTRow>
                ))}

            {isLoading &&
                skeletonArray.map((_, idx) => (
                    <LTRow key={idx} style={{ height: '44px' }}>
                        <LTItem minWidth="15%">
                            <Skeleton width={60} />
                        </LTItem>
                        <LTItem minWidth="25%">
                            <Skeleton width={160} />
                        </LTItem>
                        <LTItem minWidth="28%">
                            <Skeleton width={220} />
                        </LTItem>
                        <LTItem minWidth="18%">
                            <Skeleton width={120} />
                        </LTItem>
                        <LTItem minWidth="14%" lastItem>
                            <Skeleton width={90} />
                        </LTItem>
                    </LTRow>
                ))}
        </S.ListTable>
    );
};

export default CompaniesTable;
